// src/types/runtime.ts
// ============================================
// XEAOS Runtime Kernel Protocol
// Contains Runtime complete protocol + pure mapping function
// ============================================

export enum RuntimeName {
  Hermes = 'hermes',
  Quant = 'quant',
  Knowledge = 'knowledge',
  Business = 'business',
  Agent = 'agent',
  Content = 'content',
  Workflow = 'workflow',
  Memory = 'memory',
  Enterprise = 'enterprise',
  Integration = 'integration',
  Notification = 'notification',
  Scheduler = 'scheduler',
  // Epic 3 新增 Runtime
  Prompt = 'prompt',
  Tool = 'tool',
  // P2-2 新增 Runtime：Multi-LLM Runtime（模型无关）
  LLM = 'llm',
  // P2-5 新增 Runtime：Skill Marketplace（技能市场）
  Skill = 'skill',
  // P2-3 新增 Runtime：Multi-Workspace（多工作区隔离）
  Workspace = 'workspace',
  // Sprint 1 Task 4 新增：Content Calendar（内容排期日历）
  Calendar = 'calendar',
  // Sprint 1 Task 5 新增：AI Editorial Department（AI 编辑部，多 Agent 协作内容生产）
  Editorial = 'editorial',
  // Sprint 1 Task 8 新增：Video Pipeline（视频生成流水线：文章→脚本→分镜→TTS→数字人→字幕→合成→发布）
  VideoPipeline = 'video_pipeline',
  // Sprint 2 Task 2 新增：Knowledge Graph（真 Graph，客户→购买→SKU→SOP→Workflow→Memory→Content）
  KnowledgeGraph = 'knowledge_graph',
  // Sprint 2 Task 4 新增：AI CEO（每晚 22:00 自动生成 CEO 日报，真分析、真建议、风险预警）
  CEO = 'ceo',
  // Sprint 2 Task 5 新增：AI Sales（每日 9:00 自动跟进 CRM 客户，评分/策略/内容/多渠道触达）
  Sales = 'sales',
  // Sprint 4 Phase A 新增：CRM Runtime（Obsidian AI CRM，客户档案 Markdown 化）
  CRM = 'crm',
  // Sprint 4 Phase A 新增：SKU Runtime（产品知识库，价格/案例/销售话术 Markdown 化）
  SKU = 'sku',
  // Sprint 4 Phase A 新增：SOP Runtime（企业流程大脑，Hermes 先找 SOP 再执行）
  SOP = 'sop',
  // Sprint 4 Phase B 新增：Content Factory Runtime（内容工厂，多平台发布流水线编排）
  ContentFactory = 'content_factory',
  // Phase 4 Sprint 1 Task 1 新增：Content Calendar Runtime（内容日历，每日按时间表自动发布）
  ContentCalendar = 'content_calendar',
  // Phase 4 Sprint 1 Task 2 新增：Topic Engine Runtime（选题引擎，每日自动生成选题池）
  TopicEngine = 'topic_engine',
  // Phase 4 Sprint 1 Task 3 新增：Brand Library Runtime（品牌库，统一品牌分类与一致性检查）
  BrandLibrary = 'brand_library',
  // Phase 4 Sprint 1 Task 4 新增：Content Lifecycle Runtime（内容生命周期，一篇内容改写为多平台/电子书/课程/SKU）
  ContentLifecycle = 'content_lifecycle',
  // Sprint 3 Task 1 新增：AI News Center（AI 新闻中心，每日 09:00 自动从 10 个源采集新闻 + LLM 深度分析）
  News = 'news',
  // Sprint 6 Epic 新增：AI Business Engine（企业智能决策层，每日 21:00 生成 CEO 简报，聚合 8 个商业分析模块）
  BusinessEngine = 'business_engine',
  // Sprint 8 Epic 5 新增：Business Operator（企业运营官，每日 21:30 自动检查 8 项运营指标，主动补救）
  BusinessOperator = 'business_operator',
  // Enterprise Brain 新增：企业总脑运行时（统一目标 / 战略 / 决策 / KPI / 风险编排，每日 08:30 调度）
  EnterpriseBrain = 'enterprise_brain',
  // Wave 2 新增：Growth Engine（企业增长飞轮，A/B 测试 / 爆款复制 / 预算分配 / 品牌协同 / 增长实验）
  GrowthEngine = 'growth_engine',
  // Wave 3 新增：Company Autopilot（公司自动驾驶，自主决策 / 资源优化 / 任务重平衡 / 机会排序）
  Autopilot = 'autopilot',
  // Program A 新增：产品 Runtime stub（让 Projects 从展示升级为 Runtime Catalog，可点击进入产品）
  // Hotel SaaS - 酒店运营 Runtime（日报生成 / 入住率分析 / 收入预测 / 运营建议）
  Hotel = 'hotel',
  // Gold Factory - 黄金工厂 Runtime（生产建模 / MES 集成 / 订单管理）
  GoldFactory = 'gold_factory',
  // AI Destiny - 国学命理 Runtime（八字 / 紫微 / 奇门 / 姓名 / 择日 / 流年）
  AIDestiny = 'ai_destiny',
  // Marketing OS - 营销运营 Runtime（多平台账号矩阵 / 数据回流 / 营销自动化）
  MarketingOS = 'marketing_os',
  // v5.2 审计修复 P3-⑧：Project Runtime - 软件资产注册与调度（白皮书第十一章，酒店/量化/命理/GEO+DSO/爆款工具统一入口）
  Project = 'project',
  // Program B 新增：Strategy Brain（GEO+DSO，驱动内容生产从第一步就受策略驱动）
  // GEO Runtime - 生成式引擎优化（决定今天写什么 / 关键词 / SEO / Google / Bing / 知乎 / 公众号 / 小红书 / 抖音）
  GEO = 'geo',
  // DSO Runtime - 数字搜索优化（决定怎么分发 / 矩阵 / 品牌形成）
  DSO = 'dso',
  // GEODiagnostic Runtime - GEO 诊断（实时搜索验证 / 产品诊断 / 品牌诊断，真实 AI 轮询）
  GEODiagnostic = 'geodiagnostic',
  // GrowthFactory Runtime - 增长工厂编排（统一 GEO+DSO+Content+Brand，形成完整增长飞轮）
  GrowthFactory = 'growth_factory',
  // Program C 新增：Revenue Engine（收入闭环，AI Holding Company）
  // 统一收入统计 / SKU 业绩 / CRM 成交 / 多产品收入归因 / 收入预测
  RevenueEngine = 'revenue_engine',
  // Phase D 新增：Media Studio（媒体工作室，图文/视频/封面/九宫格/音乐生成）
  // 对接多米 API（图片+视频）+ Suno API（音乐），统一编排媒体素材生产
  // Hermes 可调用："生成封面" → generate_cover；"生成短视频" → generate_video
  MediaStudio = 'media_studio',
  // === Content Protocol（第十四章：内容协议）新增 ===
  // Content Strategy Runtime - 内容策略运行时
  //   职责：在内容工厂生成之前，先生成"策略简报（Platform Brief）"。
  //   生成流程强制：品牌选择 → 平台选择 → 内容目标 → 知识检索 → 策略简报 → 素材生成 → 发布
  //   策略简报包含：平台人设 / 目标人群 / 结构模板 / 禁忌 / 钩子 / CTA
  ContentStrategy = 'content_strategy',
  // === Knowledge Evolution Protocol（第十五章：知识演化协议）新增 ===
  // Knowledge Evolution Runtime - 知识演化运行时（第十一个 Runtime，后台运行，不占用 Workspace）
  //   职责：企业每天自动学习、沉淀、演化——扫描 Hermes 对话 / Runtime 日志 / 审核反馈 / Git Commit / 客户跟进记录
  //   两层知识体系：30_Inbox（候选知识，全自动收集）→ 夜间整理 → 正式知识库
  //   每天 23:00 自动生成"今天公司学到了什么"日报
  KnowledgeEvolution = 'knowledge_evolution',
  // === CozeWorkflowGen（爆款分析引擎 — 工作流编译器）新增 ===
  //   职责：读取 Obsidian 99_Core/Coze_Templates/*.yaml，注入用户参数，生成 Coze 工作流 JSON/YAML 配置文件
  //   本质：XEAOS 是一个"Coze 工作流编译器"——输入是用户需求，输出是 Coze 能识别的配置
  CozeWorkflowGen = 'coze_workflow_gen',
  // === CozeExecutor（爆款分析引擎 — 执行器）新增 ===
  //   职责：触发 Coze 工作流执行（或本地模拟）、接收执行结果、写入 Obsidian Content_Analysis
  //   与 CozeWorkflowGen 组成爆款分析引擎的完整 Runtime 链路
  CozeExecutor = 'coze_executor',
  // === Experience Runtime（经验飞轮闭环）新增 ===
  //   职责：内容效果 → 规则提炼经验（零 token）→ 写回 Obsidian → Planner 检索高转化经验反哺下次规划
  //   默认关闭（暂不开放），见 src/runtime/ExperienceRuntime/config.ts
  Experience = 'experience',
  // === Agent Mesh Runtime（企业 Agent 编排层）新增 ===
  //   职责：Hermes(COO) 下的 Agent 编排层 —— 意图路由 / 并行 Swarm 协同 / 数据驱动 Agent / AI 超市目录
  //   默认关闭（暂不开放），见 src/runtime/AgentMesh/config.ts
  AgentMesh = 'agent_mesh',
  // ============================================
  // V2 治理目标：L4/L5 收敛后的母体运行时（见《Runtime收敛清单.md》）
  //   说明：本轮先建骨架并迁入 Coze 样板，仍保留现有 52 个旧 Runtime 注册（渐进、不破坏）。
  //   content_pipeline = L4 内容引擎（一条流水线：选题→策略→生产→审核→发布→复盘）
  //   business_core    = L5 企业大脑（巡检→目标→决策→CEO 简报，统一 Daily 出口）
  // ============================================
  ContentPipeline = 'content_pipeline',
  BusinessCore = 'business_core',
}

// ============================================
// RuntimeStatus - Complete lifecycle
// ============================================
export type RuntimeStatus = 'idle' | 'loading' | 'ready' | 'error' | 'stopped';

// ============================================
// RuntimeHealth
// ============================================
export type RuntimeHealth = 'healthy' | 'degraded' | 'unhealthy' | 'unknown';

// ============================================
// RuntimeCapability - 运行时能力标识
// ============================================
export type RuntimeCapability =
  | 'query'
  | 'execute'
  | 'stream'
  | 'filesystem'
  | 'network'
  | 'database'
  | 'ai'
  | 'analytics'
  | 'knowledge'
  | 'business'
  | 'trading';

// ============================================
// Pure mapping function (belongs to Runtime Domain)
// ============================================
export function getHealthFromStatus(status: RuntimeStatus): RuntimeHealth {
  switch (status) {
    case 'ready':
      return 'healthy';
    case 'loading':
      return 'degraded';
    case 'error':
      return 'unhealthy';
    case 'stopped':
    case 'idle':
      return 'unknown';
    default:
      return 'unknown';
  }
}

// ============================================
// IRuntime - Core Interface
// ============================================
export interface IRuntime {
  readonly name: RuntimeName;
  readonly status: RuntimeStatus;
  initialize(): Promise<void>;
  destroy?(): Promise<void>;
  query?(params?: unknown): Promise<any>;
  execute?(command: string, params?: unknown): Promise<any>;
  getInfo(): RuntimeInfo;
  onEvent?(event: RuntimeEvent): void;
}

// ============================================
// RuntimeInfo
// ============================================
export interface RuntimeInfo {
  name: RuntimeName;
  status: RuntimeStatus;
  health: RuntimeHealth;
  version?: string;
  description?: string;
  uptime?: number;
}

// ============================================
// RuntimeResponse
// ============================================
export interface RuntimeResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  runtime: RuntimeName;
  timestamp: string;
  latency?: number;
}

// ============================================
// RuntimeEvent
// ============================================
export enum RuntimeEventType {
  // 生命周期事件
  RuntimeRegistered = 'runtime_registered',
  RuntimeReady = 'runtime_ready',
  RuntimeStopped = 'runtime_stopped',
  RuntimeError = 'runtime_error',
  
  // 视图切换事件
  ViewChanged = 'viewChanged',
  
  // Widget 事件
  WidgetChanged = 'widgetChanged',
  
  // Runtime 状态变更
  RuntimeChanged = 'runtimeChanged',
  
  // 数据更新
  DataUpdated = 'data_updated',
  
  // 意图检测
  IntentDetected = 'intent_detected',
  
  // 通知
  Notification = 'notification',
  
  // 自定义事件
  Custom = 'custom',
}

export interface RuntimeEvent {
  type: RuntimeEventType;
  source: RuntimeName;
  target?: RuntimeName;
  payload?: unknown;
  timestamp?: string;
}

export type RuntimeEventListener = (event: RuntimeEvent) => void;

// ============================================
// RuntimeConfig
// ============================================
export interface RuntimeConfig {
  id: string;
  name: string;
  description?: string;
  enabled?: boolean;
  version?: string;
  settings?: Record<string, unknown>;
}

// ============================================
// 运行时符号常量（值导出）
// 仅保留 const 值导出，不重复 export type {}（类型已通过 interface/type 声明导出）。
// 这些符号供 RuntimeManager 等模块以值方式导入，避免 verbatimModuleSyntax 下的 MISSING_EXPORT。
// ============================================
export const RuntimeStatus = Symbol('RuntimeStatus');
export const RuntimeHealth = Symbol('RuntimeHealth');
export const RuntimeCapability = Symbol('RuntimeCapability');
export const IRuntime = Symbol('IRuntime');
export const RuntimeEvent = Symbol('RuntimeEvent');
export const RuntimeInfo = Symbol('RuntimeInfo');
export const RuntimeResponse = Symbol('RuntimeResponse');
export const RuntimeEventListener = Symbol('RuntimeEventListener');
export const RuntimeConfig = Symbol('RuntimeConfig');